// src/features/admin/AdminDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import { useWorkOS } from '../workos/context/WorkOSContext';
import { Users, RefreshCw, DollarSign, AlertTriangle, X, Download, LogIn, Activity, Eye, Key, Monitor, Cpu, Search, ChevronUp, ChevronDown } from 'lucide-react';
import AIModelsTab from './AIModelsTab';
import { friendlyGpuName } from './utils/hardwareNames';

// Admin email whitelist - must match ADMIN_EMAILS env var on backend
const ADMIN_EMAILS = ['jason@isd.ai', 'julian@isd.ai'];

const AdminDashboard = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useWorkOS();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [selectedUser, setSelectedUser] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  const fetchDashboardData = useCallback(async () => {
    if (!user?.accessToken || !isAdmin) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch('/.netlify/functions/admin-dashboard', {
        headers: {
          'Authorization': `Bearer ${user.accessToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch dashboard data');
      }

      const result = await response.json();
      setData(result);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [user, isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      fetchDashboardData();
    }
  }, [isAdmin, fetchDashboardData]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Show access denied if not admin
  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-gray-400">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  const handleRefresh = () => {
    setData(null);
    fetchDashboardData();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return '-';
    if (seconds < 3600) return '<1h';
    if (seconds < 7200) return '1-2h';
    if (seconds < 14400) return '2-4h';
    if (seconds < 28800) return '4-8h';
    return `${Math.floor(seconds / 3600)}h+`;
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="w-3 h-3 inline ml-0.5" />
      : <ChevronDown className="w-3 h-3 inline ml-0.5" />;
  };

  const tabs = [
    { id: 'users', label: 'Users', icon: <Users className="w-4 h-4" /> },
    { id: 'apikeys', label: 'API Keys', icon: <Key className="w-4 h-4" /> },
    { id: 'aimodels', label: 'AI Models', icon: <Cpu className="w-4 h-4" /> },
  ];

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - ContextUI</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-gray-400 text-sm mt-1">Monitor signups and activity</p>
          </div>
          <div className="flex items-center gap-3">
            {lastRefreshed && (
              <span className="text-gray-500 text-xs">
                Updated {lastRefreshed.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {data?.stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-8">
            <div className="bg-gray-900 rounded-lg p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Total Users</p>
                  <p className="text-2xl font-bold text-white">{data.stats.totalUsers}</p>
                  <p className="text-xs text-gray-500">{data.stats.totalRegisteredUsers} registered, {data.stats.totalAnonymousUsers} local</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <DollarSign className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Credit Deposits</p>
                  <p className="text-2xl font-bold text-white">{formatCurrency(data.stats.totalRevenueCents)}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <Download className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Downloads</p>
                  <p className="text-2xl font-bold text-white">{data.stats.totalDownloads}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-500/20 rounded-lg">
                  <LogIn className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Logins</p>
                  <p className="text-2xl font-bold text-white">{data.stats.totalLogins}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-500/20 rounded-lg">
                  <Activity className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Active Today</p>
                  <p className="text-2xl font-bold text-white">{data.stats.dailyActiveUsers || 0}</p>
                  <div className="text-xs text-gray-500 mt-1">
                    <span className="text-purple-400">{data.stats.dauDesktop || 0} desktop</span>
                    <span className="mx-1">·</span>
                    <span className="text-blue-400">{data.stats.dauWeb || 0} web</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <Key className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">API Requests</p>
                  <p className="text-2xl font-bold text-white">{data.stats.totalApiRequests || 0}</p>
                  <div className="text-xs text-gray-500 mt-1">
                    <span className="text-yellow-400">{data.stats.activeApiKeys || 0} active keys</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <Monitor className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Active Installs</p>
                  <p className="text-2xl font-bold text-white">{data.stats.heartbeat?.activeInstalls24h || 0}</p>
                  <div className="text-xs text-gray-500 mt-1">
                    <span className="text-green-400">{data.stats.heartbeat?.authenticatedInstalls24h || 0} logged in</span>
                    <span className="mx-1">·</span>
                    <span className="text-gray-400">{data.stats.heartbeat?.anonymousInstalls24h || 0} anon</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-8 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-gray-900 rounded-lg p-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-gray-900 rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Loading data...</p>
            </div>
          ) : (
            <>
              {/* API Keys Tab */}
              {activeTab === 'apikeys' && data?.apiKeys && (
                <div className="overflow-auto max-h-[70vh]">
                  <table className="w-full">
                    <thead className="bg-gray-800 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Key Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Prefix</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">Requests</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Last Used</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {data.apiKeys.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                            No API keys found
                          </td>
                        </tr>
                      ) : (
                        data.apiKeys.map((key) => (
                          <tr key={key.id} className={`hover:bg-gray-800/50 ${!key.is_active ? 'opacity-50' : ''}`}>
                            <td className="px-4 py-4 whitespace-nowrap text-white font-medium">{key.name}</td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <code className="text-sm text-gray-400 font-mono">{key.key_prefix}...</code>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-gray-300 text-sm">{key.user_email || key.user_id}</td>
                            <td className="px-4 py-4 whitespace-nowrap text-center">
                              {key.is_active ? (
                                <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400">active</span>
                              ) : (
                                <span className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-400">revoked</span>
                              )}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-center">
                              <span className="text-yellow-400 font-medium">{key.request_count || 0}</span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-gray-400 text-sm">
                              {key.last_used_at ? formatDate(key.last_used_at) : '-'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-gray-400 text-sm">{formatDate(key.created_at)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* AI Models Tab */}
              {activeTab === 'aimodels' && (
                <AIModelsTab user={user} />
              )}

              {/* Users Tab */}
              {activeTab === 'users' && data?.users && (
                <div>
                  {/* Search Bar */}
                  <div className="p-4 border-b border-gray-800">
                    <div className="relative">
                      <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search by name, email, or device ID..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-gray-500"
                      />
                    </div>
                  </div>
                  <div className="overflow-auto max-h-[70vh]">
                  <table className="w-full">
                    <thead className="bg-gray-800 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => handleSort('download_count')}>Downloads <SortIcon column="download_count" /></th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => handleSort('web_login_count')}>Web Logins <SortIcon column="web_login_count" /></th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => handleSort('desktop_login_count')}>Desk Logins <SortIcon column="desktop_login_count" /></th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => handleSort('local_session_count')}>Local <SortIcon column="local_session_count" /></th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Last Login</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => handleSort('created_at')}>Signed Up <SortIcon column="created_at" /></th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Platform</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">CPU</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">GPU</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">RAM</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Screen</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">Session</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">Activity</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {data.users.length === 0 ? (
                        <tr>
                          <td colSpan={15} className="px-4 py-8 text-center text-gray-400">
                            No users found
                          </td>
                        </tr>
                      ) : (
                        (() => {
                          // Filter users by search query
                          let filteredUsers = data.users;
                          if (searchQuery) {
                            const q = searchQuery.toLowerCase();
                            filteredUsers = data.users.filter(u =>
                              u.email?.toLowerCase().includes(q) ||
                              u.display_name?.toLowerCase().includes(q) ||
                              u.devices?.some(d => d.device_id?.toLowerCase().includes(q))
                            );
                          }
                          // Sort users
                          const sortedUsers = [...filteredUsers].sort((a, b) => {
                            const dir = sortConfig.direction === 'asc' ? 1 : -1;
                            const av = a[sortConfig.key] ?? '';
                            const bv = b[sortConfig.key] ?? '';
                            if (typeof av === 'string') return dir * av.localeCompare(bv);
                            return dir * ((av || 0) - (bv || 0));
                          });
                          return sortedUsers;
                        })().flatMap((user) => {
                          const devices = user.devices || [];
                          const rowCount = Math.max(devices.length, 1);
                          const platformLabels = { win32: 'Win', darwin: 'Mac', linux: 'Linux' };

                          const renderDeviceCells = (device) => (
                            <>
                              <td className="px-4 py-3 whitespace-nowrap text-gray-300 text-sm">
                                {device ? (
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span>{platformLabels[device.platform] || device.platform}</span>
                                      {device.arch && <span className="text-gray-500">({device.arch})</span>}
                                      {device.device_id && <code className="text-[10px] text-gray-600 font-mono">{device.device_id}</code>}
                                    </div>
                                    {device.os_version && <div className="text-xs text-gray-500 truncate max-w-[200px]" title={device.os_version}>{device.os_version}</div>}
                                  </div>
                                ) : <span className="text-gray-600">-</span>}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-gray-400 text-xs max-w-[180px] truncate" title={device?.cpu_model}>
                                {device?.cpu_model || '-'}
                                {device?.cpu_cores && <span className="text-gray-500"> ({device.cpu_cores}c)</span>}
                              </td>
                              <td className="px-4 py-3 text-gray-400 text-xs max-w-[220px]"
                                title={device?.gpu_models
                                  ? device.gpu_models.map(g => g.name || `GPU ${g.vendorId}:${g.deviceId}`).join(', ')
                                  : device?.gpu_model
                                }>
                                {device?.gpu_models && device.gpu_models.length > 0 ? (
                                  <div className="flex flex-col gap-0.5">
                                    {device.gpu_models.map((g, i) => {
                                      const rawName = g.name || `GPU ${g.vendorId}:${g.deviceId}`;
                                      return (
                                        <span key={i} className={g.active ? 'text-green-400' : 'text-gray-500'}>
                                          {friendlyGpuName(rawName)}
                                          {g.active && device.gpu_models.length > 1 && <span className="text-[10px] text-gray-600 ml-1">(active)</span>}
                                        </span>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span className="truncate">{friendlyGpuName(device?.gpu_model)}</span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center text-gray-400 text-sm">
                                {device?.ram_total_gb ? <span className="text-yellow-400">{device.ram_total_gb} GB</span> : '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-gray-400 text-xs font-mono">
                                {device?.screen_resolution || '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center text-gray-400 text-sm">
                                {device?.session_duration_seconds ? <span className="text-cyan-400">{formatDuration(device.session_duration_seconds)}</span> : '-'}
                              </td>
                            </>
                          );

                          // First row: user info + first device (or dashes)
                          const isAnon = user.is_anonymous;
                          const rows = [
                            <tr key={user.id} className={`hover:bg-gray-800/50 ${isAnon ? 'opacity-75' : ''}`}>
                              <td className="px-4 py-3 whitespace-nowrap align-top" rowSpan={rowCount}>
                                <div className="flex items-center">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${isAnon ? 'bg-gradient-to-br from-gray-600 to-gray-700' : 'bg-gradient-to-br from-blue-500 to-purple-600'}`}>
                                    {isAnon ? '?' : (user.display_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?')}
                                  </div>
                                  <span className="ml-3 text-white">{isAnon ? 'Anonymous' : (user.display_name || 'No name')}</span>
                                  {isAnon && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-400 uppercase font-medium">Local</span>}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-gray-300 text-sm align-top" rowSpan={rowCount}>
                                {isAnon ? <code className="text-xs text-gray-500 font-mono">{devices[0]?.device_id || '—'}</code> : user.email}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center align-top" rowSpan={rowCount}>
                                {user.download_count > 0 ? (
                                  <div className="flex flex-col items-center gap-1">
                                    <span className="text-orange-400 font-medium">{user.download_count}</span>
                                    <div className="flex flex-wrap gap-1 justify-center">
                                      {(user.download_platforms || []).map(p => (
                                        <span key={p} className="text-xs px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">
                                          {platformLabels[p] || p}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-gray-500">0</span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center align-top" rowSpan={rowCount}>
                                <span className="text-blue-400 font-medium">{user.web_login_count || 0}</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center align-top" rowSpan={rowCount}>
                                <span className="text-purple-400 font-medium">{user.desktop_login_count || 0}</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center align-top" rowSpan={rowCount}>
                                <span className="text-amber-400 font-medium">{user.local_session_count || 0}</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-gray-400 text-sm align-top" rowSpan={rowCount}>
                                {user.last_login ? formatDate(user.last_login.date) : '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-gray-400 text-sm align-top" rowSpan={rowCount}>
                                {user.created_at ? formatDate(user.created_at) : '-'}
                              </td>
                              {renderDeviceCells(devices[0] || null)}
                              <td className="px-4 py-3 whitespace-nowrap text-center align-top" rowSpan={rowCount}>
                                {isAnon ? (
                                  <span className="text-gray-600">-</span>
                                ) : (
                                  <button
                                    onClick={() => setSelectedUser(user)}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs text-gray-300 transition-colors"
                                  >
                                    <span className="text-teal-400 font-medium">{user.login_count || 0}</span>
                                    <Eye className="w-3 h-3" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ];

                          // Additional device rows (only hardware columns, user columns spanned)
                          for (let i = 1; i < devices.length; i++) {
                            rows.push(
                              <tr key={`${user.id}-device-${i}`} className="hover:bg-gray-800/50 bg-gray-900/50">
                                {renderDeviceCells(devices[i])}
                              </tr>
                            );
                          }

                          return rows;
                        })
                      )}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Activity Detail Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
                <div>
                  <h3 className="text-lg font-semibold text-white">Activity History</h3>
                  <p className="text-sm text-gray-400">{selectedUser.email}</p>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-800 rounded-lg p-4">
                    <p className="text-gray-400 text-sm">Total Logins</p>
                    <p className="text-2xl font-bold text-teal-400">{selectedUser.login_count || 0}</p>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-4">
                    <p className="text-gray-400 text-sm">Web</p>
                    <p className="text-2xl font-bold text-blue-400">{selectedUser.web_login_count || 0}</p>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-4">
                    <p className="text-gray-400 text-sm">Desktop</p>
                    <p className="text-2xl font-bold text-purple-400">{selectedUser.desktop_login_count || 0}</p>
                  </div>
                </div>

                <h4 className="text-sm font-medium text-gray-400 uppercase mb-3">Recent Logins</h4>
                <div className="space-y-2">
                  {data?.loginEvents?.filter(a => a.user_id === selectedUser.id).slice(0, 50).map((login, idx) => (
                    <div key={login.id || idx} className="flex items-center justify-between py-2 px-3 bg-gray-800/50 rounded">
                      <div className="flex items-center gap-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          login.source === 'desktop' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {login.source}
                        </span>
                        <span className="text-gray-300 text-sm">{login.auth_type || 'login'}</span>
                      </div>
                      <span className="text-gray-500 text-xs">{formatDate(login.created_at)}</span>
                    </div>
                  ))}
                  {(!data?.loginEvents?.filter(a => a.user_id === selectedUser.id).length) && (
                    <p className="text-gray-500 text-center py-4">No logins recorded yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminDashboard;
